import { useState, useEffect, useCallback } from "react";
import API from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Calendar, ChevronLeft, ChevronRight, Clock, MapPin, Plus, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";

const EVENT_TYPES = [
  { v: "appointment", label: "Appointment" },
  { v: "inspection",  label: "Inspection" },
  { v: "follow_up",   label: "Follow-up" },
  { v: "job",         label: "Job" },
  { v: "other",       label: "Other" },
];

const STATUS_LABELS = { accepted: "Accepted", en_route: "On Route", arrived: "Arrived", in_progress: "On Site" };

export default function TechSchedule() {
  const { user } = useAuth();
  const vendorId = user?.vendor?.id;
  const [schedule, setSchedule] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [activeJobs, setActiveJobs] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState("week"); // "day" | "week"
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(null); // {date:"YYYY-MM-DD"} or null
  const [saving, setSaving] = useState(false);

  const getDateRange = useCallback(() => {
    const d = new Date(currentDate);
    if (view === "day") {
      const ds = d.toISOString().split("T")[0];
      return { from: ds, to: ds };
    }
    const day = d.getDay();
    const start = new Date(d);
    start.setDate(d.getDate() - day);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return { from: start.toISOString().split("T")[0], to: end.toISOString().split("T")[0] };
  }, [currentDate, view]);

  const fetchData = useCallback(async () => {
    const { from, to } = getDateRange();
    try {
      const [schedRes, calRes] = await Promise.allSettled([
        API.get(`/schedule/tech?date_from=${from}&date_to=${to}`),
        vendorId
          ? API.get(`/calendar/${vendorId}`, {
              params: {
                start: new Date(from + "T00:00:00Z").toISOString(),
                end: new Date(to + "T23:59:59Z").toISOString(),
              },
            })
          : Promise.resolve({ data: { events: [] } }),
      ]);
      if (schedRes.status === "fulfilled") {
        setSchedule(schedRes.value.data.schedule);
        setActiveJobs(schedRes.value.data.active_jobs);
      }
      if (calRes.status === "fulfilled") {
        setCalendarEvents(calRes.value.data.events || []);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [getDateRange, vendorId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const navigate = (dir) => {
    const d = new Date(currentDate);
    d.setDate(d.getDate() + (view === "day" ? dir : dir * 7));
    setCurrentDate(d);
  };

  const today = new Date().toISOString().split("T")[0];
  const weekDays = [];
  if (view === "week") {
    const d = new Date(currentDate);
    const day = d.getDay();
    const start = new Date(d);
    start.setDate(d.getDate() - day);
    for (let i = 0; i < 7; i++) {
      const wd = new Date(start);
      wd.setDate(start.getDate() + i);
      weekDays.push(wd);
    }
  }

  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const getItemsForDate = (dateStr) => {
    const scheduled = schedule.filter(s => s.scheduled_date === dateStr).map(s => ({
      ...s, _kind: "scheduled",
    }));
    const calForDay = calendarEvents
      .filter(ev => !ev.is_deleted && (ev.start_datetime || "").slice(0, 10) === dateStr)
      .map(ev => ({
        id: ev.id,
        _kind: "calendar",
        scheduled_date: dateStr,
        scheduled_time: new Date(ev.start_datetime).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }),
        duration_minutes: Math.max(
          Math.round((new Date(ev.end_datetime) - new Date(ev.start_datetime)) / 60000),
          0,
        ),
        status: ev.status || "scheduled",
        service_type_label: ev.title,
        customer_name: ev.event_type || "appointment",
        property_address: ev.description || "",
        notes: ev.description || "",
        event_type: ev.event_type,
      }));
    return [...scheduled, ...calForDay].sort((a, b) =>
      (a.scheduled_time || "").localeCompare(b.scheduled_time || ""));
  };

  const openCreateForDate = (date) => {
    if (!vendorId) {
      toast.error("Schedule requires a technician account");
      return;
    }
    setCreating({ date });
  };

  const saveNewEvent = async (form) => {
    if (!vendorId) {
      toast.error("Your technician profile isn't loaded yet. Please refresh and try again.");
      return;
    }
    if (!form.title || !form.title.trim()) {
      toast.error("Please enter a title for the event");
      return;
    }
    if (!form.date) {
      toast.error("Please pick a date");
      return;
    }
    setSaving(true);
    try {
      const start = new Date(`${form.date}T${form.start_time || "09:00"}`);
      const end   = new Date(`${form.date}T${form.end_time   || "10:00"}`);
      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        toast.error("Invalid date or time format");
        setSaving(false);
        return;
      }
      if (end <= start) {
        toast.error("End time must be after start time");
        setSaving(false);
        return;
      }
      await API.post("/calendar", {
        title: form.title.trim(),
        description: form.notes || "",
        event_type: form.event_type,
        vendor_id: vendorId,
        request_id: form.request_id || null,
        start_datetime: start.toISOString(),
        end_datetime: end.toISOString(),
        all_day: false,
        status: "scheduled",
      });
      toast.success("Schedule entry saved");
      setCreating(null);
      fetchData();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Save failed";
      toast.error(msg, { duration: 6000 });
      console.error("Schedule save failed:", e);
    } finally { setSaving(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-6 h-6 border-2 border-zinc-900 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-4" data-testid="tech-schedule">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-heading font-bold text-zinc-900">Schedule</h2>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            onClick={() => openCreateForDate(currentDate.toISOString().split("T")[0])}
            className="h-8 bg-red-600 hover:bg-red-700 text-white"
            data-testid="sched-add-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Add
          </Button>
          <div className="flex gap-1">
            <button onClick={() => setView("day")}
              className={`px-2.5 py-1 rounded-lg text-xs font-medium ${view === "day" ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600"}`}>
              Day
            </button>
            <button onClick={() => setView("week")}
              className={`px-2.5 py-1 rounded-lg text-xs font-medium ${view === "week" ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600"}`}>
              Week
            </button>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <Button size="sm" variant="outline" onClick={() => navigate(-1)} className="h-8 w-8 p-0">
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <div className="text-center">
          {view === "day" ? (
            <p className="text-sm font-medium text-zinc-800">
              {currentDate.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
            </p>
          ) : (
            <p className="text-sm font-medium text-zinc-800">
              {weekDays[0]?.toLocaleDateString("en-US", { month: "short", day: "numeric" })} — {weekDays[6]?.toLocaleDateString("en-US", { month: "short", day: "numeric" })}
            </p>
          )}
        </div>
        <Button size="sm" variant="outline" onClick={() => navigate(1)} className="h-8 w-8 p-0">
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      {/* Active Jobs */}
      {activeJobs.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-[10px] text-zinc-500 font-bold uppercase">Active Jobs</p>
          {activeJobs.map(job => (
            <Card key={job.id} className="border border-red-200 bg-red-50/30" data-testid={`active-job-${job.id}`}>
              <CardContent className="p-3 flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-zinc-800">{job.service_type_label}</p>
                  <p className="text-[10px] text-zinc-500">{job.customer_name} - {job.property_address_line1}</p>
                </div>
                <Badge className="bg-red-100 text-red-700 text-[9px]">{STATUS_LABELS[job.current_status] || job.current_status}</Badge>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Week View */}
      {view === "week" && (
        <div className="grid grid-cols-7 gap-1">
          {weekDays.map((wd, i) => {
            const ds = wd.toISOString().split("T")[0];
            const isToday = ds === today;
            const dayItems = getItemsForDate(ds);
            return (
              <button key={i} type="button"
                onClick={() => openCreateForDate(ds)}
                className={`min-h-[80px] p-1.5 rounded-lg border text-left cursor-pointer hover:bg-red-50/40 transition-colors ${isToday ? "border-red-300 bg-red-50/30" : "border-zinc-100 bg-zinc-50"}`}
                data-testid={`cal-day-${ds}`}>
                <p className={`text-[10px] font-bold text-center mb-1 ${isToday ? "text-red-600" : "text-zinc-400"}`}>
                  {dayNames[i]}
                </p>
                <p className={`text-xs text-center mb-1 ${isToday ? "text-red-700 font-bold" : "text-zinc-600"}`}>
                  {wd.getDate()}
                </p>
                {dayItems.map(s => (
                  <div key={s.id} className={`${s._kind === "calendar" ? "bg-emerald-100" : "bg-blue-100"} rounded p-1 mb-0.5`}>
                    <p className={`text-[8px] font-bold truncate ${s._kind === "calendar" ? "text-emerald-800" : "text-blue-800"}`}>{s.scheduled_time || "TBD"}</p>
                    <p className={`text-[8px] truncate ${s._kind === "calendar" ? "text-emerald-700" : "text-blue-600"}`}>{s.service_type_label}</p>
                  </div>
                ))}
                {dayItems.length === 0 && (
                  <p className="text-[9px] text-zinc-400 text-center mt-2 opacity-60">+ tap</p>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Day View */}
      {view === "day" && (
        <div className="space-y-2">
          <Button onClick={() => openCreateForDate(currentDate.toISOString().split("T")[0])}
                  size="sm"
                  className="w-full bg-red-600 hover:bg-red-700 text-white text-xs"
                  data-testid="sched-add-event">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add Event to This Day
          </Button>
          {getItemsForDate(currentDate.toISOString().split("T")[0]).length > 0 ? (
            getItemsForDate(currentDate.toISOString().split("T")[0]).map(s => (
              <Card key={s.id} className={`border ${s._kind === "calendar" ? "border-emerald-200" : "border-zinc-200"}`} data-testid={`sched-item-${s.id}`}>
                <CardContent className="p-3">
                  <div className="flex items-center gap-2 mb-1.5">
                    <Clock className="w-3.5 h-3.5 text-zinc-400" />
                    <span className="text-xs font-bold text-zinc-800">{s.scheduled_time || "TBD"}</span>
                    {s.duration_minutes > 0 && (
                      <Badge className="text-[9px] bg-blue-100 text-blue-700">{s.duration_minutes}min</Badge>
                    )}
                    <Badge className={`text-[9px] ${s.status === "scheduled" ? "bg-zinc-100 text-zinc-600" : "bg-emerald-100 text-emerald-700"}`}>
                      {s.status}
                    </Badge>
                    {s._kind === "calendar" && (
                      <Badge className="text-[9px] bg-emerald-100 text-emerald-700 ml-auto">Schedule</Badge>
                    )}
                  </div>
                  <p className="text-sm font-medium text-zinc-900">{s.service_type_label}</p>
                  {(s.customer_name || s.property_address) && (
                    <div className="flex items-center gap-1.5 mt-1">
                      <MapPin className="w-3 h-3 text-zinc-400" />
                      <span className="text-[10px] text-zinc-500">{s.customer_name}{s.property_address ? ` — ${s.property_address}` : ""}</span>
                    </div>
                  )}
                  {s.notes && <p className="text-[10px] text-zinc-400 mt-1">{s.notes}</p>}
                </CardContent>
              </Card>
            ))
          ) : (
            <button type="button"
                    onClick={() => openCreateForDate(currentDate.toISOString().split("T")[0])}
                    className="w-full text-center py-8 border border-dashed border-zinc-200 rounded-xl hover:bg-zinc-50 transition-colors"
                    data-testid="sched-empty-add">
              <Calendar className="w-8 h-8 text-zinc-300 mx-auto mb-2" />
              <p className="text-sm text-zinc-400">Tap to add an event for this day</p>
            </button>
          )}
        </div>
      )}

      {creating && (
        <NewEventDialog
          date={creating.date}
          activeJobs={activeJobs}
          saving={saving}
          onSave={saveNewEvent}
          onClose={() => setCreating(null)}
        />
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
function NewEventDialog({ date, activeJobs = [], saving, onSave, onClose }) {
  const [form, setForm] = useState({
    date,
    title: "",
    event_type: "appointment",
    start_time: "09:00",
    end_time: "10:00",
    notes: "",
    request_id: "",
  });

  const onPickJob = (val) => {
    if (val === "none") {
      setForm((f) => ({ ...f, request_id: "", event_type: f.event_type }));
      return;
    }
    const job = activeJobs.find((j) => j.id === val);
    if (!job) {
      setForm((f) => ({ ...f, request_id: val }));
      return;
    }
    setForm((f) => ({
      ...f,
      request_id: job.id,
      title: f.title?.trim() ? f.title : (job.service_type_label || "Job visit"),
      event_type: "job",
    }));
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="sched-new-event-dialog">
        <DialogHeader>
          <DialogTitle>New Schedule Entry</DialogTitle>
          <DialogDescription className="text-xs">
            Saved to your schedule — your manager can see it too.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 text-xs">
          {activeJobs.length > 0 && (
            <div>
              <label className="text-[10px] uppercase text-zinc-500">Link to active job (optional)</label>
              <Select value={form.request_id || "none"} onValueChange={onPickJob}>
                <SelectTrigger data-testid="sched-job-picker"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No active job</SelectItem>
                  {activeJobs.map((j) => (
                    <SelectItem key={j.id} value={j.id}>
                      {j.service_type_label} — {j.customer_name || "Customer"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <label className="text-[10px] uppercase text-zinc-500">Title</label>
            <Input value={form.title} placeholder="Event title"
                   onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                   data-testid="sched-title" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase text-zinc-500">Date</label>
              <Input type="date" value={form.date}
                     onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                     data-testid="sched-date" />
            </div>
            <div>
              <label className="text-[10px] uppercase text-zinc-500">Type</label>
              <Select value={form.event_type}
                      onValueChange={v => setForm(f => ({ ...f, event_type: v }))}>
                <SelectTrigger data-testid="sched-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {EVENT_TYPES.map(t => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase text-zinc-500">Start time</label>
              <Input type="time" value={form.start_time}
                     onChange={e => setForm(f => ({ ...f, start_time: e.target.value }))}
                     data-testid="sched-start" />
            </div>
            <div>
              <label className="text-[10px] uppercase text-zinc-500">End time</label>
              <Input type="time" value={form.end_time}
                     onChange={e => setForm(f => ({ ...f, end_time: e.target.value }))}
                     data-testid="sched-end" />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase text-zinc-500">Notes</label>
            <Textarea rows={2} value={form.notes}
                      onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                      placeholder="Optional notes"
                      data-testid="sched-notes" />
          </div>
          <div className="flex gap-2 pt-1">
            <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
            <Button onClick={() => onSave(form)}
                    disabled={!form.title || saving}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                    data-testid="sched-save">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <><Save className="w-3.5 h-3.5 mr-1" /> Save</>}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
